import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Edit2, Check, X, User, Calendar, Users, Heart, Palette, MessageSquare, Settings, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const StoryParametersBanner = ({ 
  parameters, 
  onParameterUpdate, 
  editable = true,
  highlightField = null,
  onExpandChange,
  className = "",
  headerText = null,
  disableAnimations = false
}) => {
  const { tr } = useLanguage();
  const bannerTitle = headerText || tr('Customize your bedtime story', 'Personnalisez votre histoire du soir');
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [currentActiveField, setCurrentActiveField] = useState('name');
  const [completedFields, setCompletedFields] = useState([]);
  
  const fieldOrder = ['name', 'age', 'gender', 'interests', 'style', 'lesson'];

  const isFieldAccessible = (fieldName) => {
    const fieldIndex = fieldOrder.indexOf(fieldName);
    const currentIndex = fieldOrder.indexOf(currentActiveField);
    return fieldIndex <= currentIndex || completedFields.includes(fieldName);
  };

  // Notify parent when expand state changes
  const toggleExpanded = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    if (onExpandChange) {
      onExpandChange(newExpandedState);
    }
  };

  const languageOptions = [
    { value: 'en-US', label: 'English' },
    { value: 'fr-FR', label: 'Français' }
  ];

  const interests = [
    { value: 'Animals', fr: 'Animaux' },
    { value: 'Space & Stars', fr: 'Espace & Étoiles' },
    { value: 'Ocean & Sea Life', fr: 'Océan & Vie marine' },
    { value: 'Dinosaurs', fr: 'Dinosaures' },
    { value: 'Magic & Fantasy', fr: 'Magie & Fantaisie' },
    { value: 'Sports', fr: 'Sports' },
    { value: 'Music', fr: 'Musique' },
    { value: 'Art & Drawing', fr: 'Art & Dessin' },
    { value: 'Nature & Forest', fr: 'Nature & Forêt' },
    { value: 'Superheroes', fr: 'Super-héros' },
    { value: 'Vehicles & Transportation', fr: 'Véhicules & Transports' },
    { value: 'Cooking & Food', fr: 'Cuisine & Nourriture' },
    { value: 'Science & Experiments', fr: 'Science & Expériences' }
  ];
  const getInterestLabel = (v) => {
    const found = interests.find(i => i.value === v);
    return found ? tr(found.value, found.fr) : v;
  };

  const styles = [
    { value: 'funny', label: tr('Funny & Silly', 'Drôle & Loufoque'), emoji: '😄' },
    { value: 'adventurous', label: tr('Adventurous & Exciting', 'Aventureux & Palpitant'), emoji: '🌟' },
    { value: 'gentle', label: tr('Gentle & Calming', 'Doux & Apaisant'), emoji: '🌙' },
    { value: 'magical', label: tr('Magical & Enchanting', 'Magique & Enchanteur'), emoji: '✨' },
    { value: 'educational', label: tr('Educational & Learning', 'Éducatif & Instructif'), emoji: '📚' }
  ];

  const genderOptions = [
    { value: 'boy', label: tr('Boy', 'Garçon') },
    { value: 'girl', label: tr('Girl', 'Fille') },
    { value: 'other', label: tr('Other', 'Autre') }
  ];

  const getCardClasses = (fieldName) => {
    const baseClasses = "bg-white rounded-2xl p-4 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300";
    
    if (disableAnimations) {
      return baseClasses;
    }
    
    if (completedFields.includes(fieldName)) {
      return `${baseClasses} ring-4 ring-blue-400 border-blue-200`;
    }
    if (fieldName === currentActiveField) {
      // If editing, show static blue ring, otherwise animate
      const isEditing = editingField === fieldName;
      if (isEditing) {
        return `${baseClasses} ring-4 ring-blue-400 ring-offset-2 ring-offset-white border-blue-200`;
      } else {
        return `${baseClasses} ring-4 ring-blue-400 ring-offset-2 ring-offset-white animate-bounce border-blue-200`;
      }
    }
    if (!isFieldAccessible(fieldName)) {
      return `${baseClasses} opacity-50 pointer-events-none`;
    }
    if (highlightField === fieldName) {
      return `${baseClasses} ring-4 ring-blue-400 ring-offset-2 ring-offset-white animate-bounce border-blue-200`;
    }
    return baseClasses;
  };

  const getIconClasses = (fieldName) => {
    const baseClasses = "w-6 h-6 rounded-lg flex items-center justify-center transition-all duration-300";
    
    if (highlightField === fieldName) {
      return `${baseClasses} animate-bounce shadow-lg`;
    }
    return baseClasses;
  };

  const ageGroups = ['3-4', '5-7', '8-10', '11-13'];

  const startEditing = (field, currentValue) => {
    if (!editable || !isFieldAccessible(field)) return;
    setEditingField(field);
    setTempValues(prev => ({
      ...prev,
      [field]: currentValue
    }));
  };

  const saveEdit = (field) => {
    if (onParameterUpdate && tempValues[field] !== undefined) {
      onParameterUpdate(field, tempValues[field]);
    }
    
    // Ajouter aux champs complétés et passer au suivant
    if (!completedFields.includes(field)) {
      setCompletedFields(prev => [...prev, field]);
    }
    
    const currentIndex = fieldOrder.indexOf(field);
    const nextIndex = currentIndex + 1;
    if (nextIndex < fieldOrder.length) {
      setCurrentActiveField(fieldOrder[nextIndex]);
    }
    
    setEditingField(null);
    setTempValues(prev => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  const cancelEdit = () => {
    setEditingField(null);
    setTempValues({});
  };

  const getStyleLabel = (styleValue) => {
    const style = styles.find(s => s.value === styleValue);
    return style ? `${style.emoji} ${style.label}` : styleValue;
  };

  const getGenderLabel = (genderValue) => {
    const gender = genderOptions.find(g => g.value === genderValue);
    return gender ? gender.label : genderValue;
  };

  const renderEditableField = (field, value, renderContent, renderEdit) => {
    const isEditing = editingField === field;

    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveEdit(field);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelEdit();
      }
    };

    return (
      <div className="relative group">
        <div className="flex items-center justify-between">
          {isEditing ? (
            <div onKeyDown={handleKeyDown} className="flex-1">
              {renderEdit()}
            </div>
          ) : (
            <div 
              className={`flex-1 ${editable && isFieldAccessible(field) ? 'cursor-pointer hover:bg-indigo-50/50 rounded-lg px-2 py-1.5 transition-all duration-200' : ''}`}
              onClick={() => startEditing(field, value)}
            >
              {renderContent()}
            </div>
          )}
          
          {editable && !isEditing && isFieldAccessible(field) && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <Edit2 
                className="w-3.5 h-3.5 text-gray-400 hover:text-indigo-500 transition-colors cursor-pointer ml-2"
                onClick={() => startEditing(field, value)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-gradient-to-r from-slate-50 to-gray-50 border-t border-gray-200/50 rounded-lg ${className}`}>
      {/* Header - Always visible with modern glass morphism effect */}
      <div 
        className="px-6 py-4 cursor-pointer hover:bg-white/60 transition-all duration-300 backdrop-blur-sm"
        onClick={toggleExpanded}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Settings className="w-4 h-4 text-white" />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-800 tracking-wide">
                {bannerTitle}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="bg-white/70 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-gray-200/50 shadow-sm">
              <div className="text-xs font-medium text-gray-600">
                {parameters?.name && (
                  <span className="text-indigo-600 font-semibold">{parameters.name}</span>
                )}
                {parameters?.age && (
                  <span className="text-gray-500 ml-1">• {parameters.age} {tr('years', 'ans')}</span>
                )}
              </div>
            </div>
            <div className={`p-2 rounded-lg transition-all duration-200 ${isExpanded ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}>
              {isExpanded ? 
                <ChevronUp className="w-4 h-4" /> : 
                <ChevronDown className="w-4 h-4" />
              }
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content with modern card-based layout */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100/80 bg-white/40 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
            
            {/* Name Field */}
            <div className={getCardClasses('name')}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`${getIconClasses('name')} bg-gradient-to-r from-blue-500 to-cyan-500`}>
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">{tr('Name', 'Prénom')}</span>
              </div>
              {renderEditableField(
                'name',
                parameters?.name,
                () => (
                  <div className="text-sm font-semibold text-gray-900">
                    {parameters?.name || (
                      <span className="text-gray-400 italic">{tr('Not specified', 'Non spécifié')}</span>
                    )}
                  </div>
                ),
                () => (
                  <input
                    type="text"
                    value={tempValues.name || ''}
                    onChange={(e) => setTempValues(prev => ({ ...prev, name: e.target.value }))}
                    className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={tr("Child's name", "Prénom de l'enfant")}
                    autoFocus
                  />
                )
              )}
            </div>

            {/* Age Field */}
            <div className={getCardClasses('age')}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`${getIconClasses('age')} bg-gradient-to-r from-green-500 to-emerald-500`}>
                  <Calendar className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">{tr('Age Range', "Tranche d'âge")}</span>
              </div>
              {renderEditableField(
                'age',
                parameters?.age,
                () => (
                  <div className="text-sm font-semibold text-gray-900">
                    {parameters?.age ? `${parameters.age} ${tr('years', 'ans')}` : (
                      <span className="text-gray-400 italic">{tr('Not specified', 'Non spécifié')}</span>
                    )}
                  </div>
                ),
                () => (
                  <select
                    value={tempValues.age || ''}
                    onChange={(e) => setTempValues(prev => ({ ...prev, age: e.target.value }))}
                    className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  >
                    <option value="">{tr('Select age...', "Choisir l'âge...")}</option>
                    {ageGroups.map(age => (
                      <option key={age} value={age}>{age} {tr('years', 'ans')}</option>
                    ))}
                  </select>
                )
              )}
            </div>

            {/* Gender Field */}
            <div className={getCardClasses('gender')}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`${getIconClasses('gender')} bg-gradient-to-r from-purple-500 to-pink-500`}>
                  <Users className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">{tr('Type', 'Type')}</span>
              </div>
              {renderEditableField(
                'gender',
                parameters?.gender,
                () => (
                  <div className="text-sm font-semibold text-gray-900">
                    {parameters?.gender ? getGenderLabel(parameters.gender) : (
                      <span className="text-gray-400 italic">{tr('Not specified', 'Non spécifié')}</span>
                    )}
                  </div>
                ),
                () => (
                  <select
                    value={tempValues.gender || ''}
                    onChange={(e) => setTempValues(prev => ({ ...prev, gender: e.target.value }))}
                    className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  >
                    <option value="">{tr('Select...', 'Choisir...')}</option>
                    {genderOptions.map(gender => (
                      <option key={gender.value} value={gender.value}>{gender.label}</option>
                    ))}
                  </select>
                )
              )}
            </div>

            {/* Interest Field */}
            <div className={getCardClasses('interests')}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`${getIconClasses('interests')} bg-gradient-to-r from-orange-500 to-red-500`}>
                  <Heart className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">{tr('Interest', 'Centre d\'intérêt')}</span>
              </div>
              {renderEditableField(
                'interests',
                parameters?.interests,
                () => (
                  <div className="text-sm font-semibold text-gray-900">
                    {parameters?.interests && parameters.interests.length > 0 
                      ? getInterestLabel(parameters.interests[0]) 
                      : <span className="text-gray-400 italic">{tr('Not specified', 'Non spécifié')}</span>}
                  </div>
                ),
                () => (
                  <select
                    value={tempValues.interests?.[0] || ''}
                    onChange={(e) => setTempValues(prev => ({ 
                      ...prev, 
                      interests: e.target.value ? [e.target.value] : []
                    }))}
                    className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  >
                    <option value="">{tr('Select...', 'Choisir...')}</option>
                    {interests.map(interest => (
                      <option key={interest.value} value={interest.value}>{tr(interest.value, interest.fr)}</option>
                    ))}
                  </select>
                )
              )}
            </div>

            {/* Style/Tone Field */}
            <div className={getCardClasses('style')}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`${getIconClasses('style')} bg-gradient-to-r from-indigo-500 to-purple-500`}>
                  <Palette className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">{tr('Tone', 'Ton')}</span>
              </div>
              {renderEditableField(
                'style',
                parameters?.style,
                () => (
                  <div className="text-sm font-semibold text-gray-900">
                    {parameters?.style ? getStyleLabel(parameters.style) : (
                      <span className="text-gray-400 italic">{tr('Not specified', 'Non spécifié')}</span>
                    )}
                  </div>
                ),
                () => (
                  <select
                    value={tempValues.style || ''}
                    onChange={(e) => setTempValues(prev => ({ ...prev, style: e.target.value }))}
                    className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  >
                    <option value="">{tr('Select...', 'Choisir...')}</option>
                    {styles.map(style => (
                      <option key={style.value} value={style.value}>
                        {style.emoji} {style.label}
                      </option>
                    ))}
                  </select>
                )
              )}
            </div>

            {/* Message/Lesson Field */}
            <div className={getCardClasses('lesson')}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`${getIconClasses('lesson')} bg-gradient-to-r from-teal-500 to-cyan-500`}>
                  <MessageSquare className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">{tr('Message', 'Message')}</span>
              </div>
              {renderEditableField(
                'lesson',
                parameters?.lesson,
                () => (
                  <div className="text-sm font-semibold text-gray-900 line-clamp-2">
                    {parameters?.lesson || (
                      <span className="text-gray-400 italic">{tr('Not specified', 'Non spécifié')}</span>
                    )}
                  </div>
                ),
                () => (
                  <textarea
                    value={tempValues.lesson || ''}
                    onChange={(e) => setTempValues(prev => ({ ...prev, lesson: e.target.value }))}
                    className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-2 w-full resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={tr('What lesson to include in the story?', "Quelle leçon inclure dans l'histoire ?")}
                    rows="2"
                    autoFocus
                  />
                )
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default StoryParametersBanner;